import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import jobService, { NO_JOBS } from '../../services/jobs';
import inductionService, { inductionRefusal } from '../../services/induction';
import type { InductionGap } from '../../types/induction';
import type {
  Equipment, ChecklistTemplate, MaintenanceInterval,
  EquipmentAttachment,
} from '../../types/equipment';
import type { JobListItem } from '../../types/jobs';
import { useAuth } from '../../context/AuthContext';
import {
  XCircle, AlertTriangle, ChevronRight,
  Wrench, Gauge, Play, Check, ClipboardList, Plus, ShieldAlert,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { toNumOr } from '@/utils/numeric';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'job' | 'meter' | 'implements' | 'checklist';
const STEPS: Step[] = ['job', 'meter', 'implements', 'checklist'];

interface CheckItem {
  type: 'template' | 'maintenance';
  template_item_id?: number;
  interval_id?: number;
  item_text: string;
  is_required: boolean;
  is_overdue?: boolean;
  passed: boolean | null;
  notes: string;
}

/**
 * One machine's pre-start, held separately from the others.
 *
 * A pre-start is an inspection of a machine, not of a job. The tractor and
 * the implement behind it have their own guards, hoses, bearings and hitch,
 * and their own checklist template keyed on equipment type — so each gets its
 * own screen and its own completion record. Running them as one list on one
 * screen is how an operator ticks the tractor's brakes against the sprayer.
 */
interface MachineCheck {
  equipment_id: number;
  name: string;
  equipment_type: string;
  role: 'primary' | 'implement';
  templates: ChecklistTemplate[];
  selectedTemplateId: number | null;
  dueItems: MaintenanceInterval[];
  items: CheckItem[];
}

// Placeholder id used for the "Quick use" virtual option in the job picker.
const QUICK_JOB_ID = -1;

// ─── Component ───────────────────────────────────────────────────────────────

export default function PreStartFlow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const eqId = parseInt(id!);
  const preselectedJobId = searchParams.get('job_id')
    ? parseInt(searchParams.get('job_id')!)
    : null;

  const [equipment, setEquipment]     = useState<Equipment | null>(null);
  const [attachments, setAttachments] = useState<EquipmentAttachment[]>([]);
  const [templates, setTemplates]     = useState<ChecklistTemplate[]>([]);
  const [dueItems, setDueItems]       = useState<MaintenanceInterval[]>([]);
  const [jobs, setJobs]               = useState<JobListItem[]>([]);

  const [step, setStep]               = useState<Step>('job');
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [hoursStart, setHoursStart]   = useState('');
  const [kmStart, setKmStart]         = useState('');
  const [taskDesc, setTaskDesc]       = useState('');
  const [selectedImpl, setSelectedImpl] = useState<Set<number>>(new Set());
  /** One entry per machine going out, in the order they are walked. */
  const [machines, setMachines]       = useState<MachineCheck[]>([]);
  const [machineIdx, setMachineIdx]   = useState(0);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Is the operator inducted on everything going out? Asked up front, so
  // nobody works through a whole checklist to be refused at the last tap.
  // The server's gate on start is what actually decides.
  const [inductionGaps, setInductionGaps] = useState<InductionGap[]>([]);
  const [canSelfInduct, setCanSelfInduct] = useState(false);
  const [selfInduct, setSelfInduct]       = useState(false);
  const [asked, setAsked]                 = useState<'idle' | 'asking' | 'asked' | 'failed'>('idle');
  const implKey = Array.from(selectedImpl).sort().join(',');
  useEffect(() => {
    if (!user?.id || !eqId) return;
    let cancelled = false;
    const ids = [eqId, ...(implKey ? implKey.split(',').map(Number) : [])];
    Promise.all([inductionService.check([user.id], ids), inductionService.access()])
      .then(([check, access]) => {
        if (cancelled) return;
        setInductionGaps(check.gaps);
        setCanSelfInduct(access.can_assess);
      })
      .catch(() => { if (!cancelled) setInductionGaps([]); });
    return () => { cancelled = true; };
  }, [user?.id, eqId, implKey]);
  const blockedByInduction = inductionGaps.length > 0 && !(canSelfInduct && selfInduct
    && inductionGaps.every(g => g.reason !== 'certificate'));

  useEffect(() => {
    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.getAttachments(eqId),
      equipmentService.getChecklistTemplates(eqId),
      equipmentService.getDueMaintenanceItems(eqId),
      // Caught on its own: with no job list the flow still offers Quick use.
      user?.id
        ? jobService.getJobs({ assigned_to: user.id, equipment_id: eqId, status: 'assigned', limit: 20 })
            .catch(() => NO_JOBS)
        : Promise.resolve(NO_JOBS),
    ]).then(([eqRes, attRes, tmplRes, dueRes, jobsRes]) => {
      if (eqRes.success && eqRes.data) {
        const eq = eqRes.data;
        setEquipment(eq);
        if (eq.current_hours != null) setHoursStart(eq.current_hours.toString());
        if (eq.current_km    != null) setKmStart(eq.current_km.toString());
      }
      if (attRes.success && attRes.data) {
        setAttachments(attRes.data);
        setSelectedImpl(new Set(attRes.data.map(a => a.implement_id)));
      }
      if (tmplRes.success && tmplRes.data) setTemplates(tmplRes.data);
      if (dueRes.success && dueRes.data) setDueItems(dueRes.data);
      if (jobsRes.success && jobsRes.data) {
        const list = jobsRes.data.jobs || [];
        setJobs(list);

        // If caller pre-selected a job via ?job_id=, jump straight past the picker.
        if (preselectedJobId != null && list.some(j => j.id === preselectedJobId)) {
          setSelectedJobId(preselectedJobId);
          setStep('meter');
        } else if (list.length === 0) {
          // No assigned jobs → default to Quick use and skip to meter.
          setSelectedJobId(QUICK_JOB_ID);
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId, user?.id, preselectedJobId]);

  function isOverdue(mi: MaintenanceInterval, hrs: number, km: number) {
    if (mi.interval_type === 'hours' && mi.next_due_hours != null) return hrs >= toNumOr(mi.next_due_hours);
    if (mi.interval_type === 'km'    && mi.next_due_km    != null) return km >= toNumOr(mi.next_due_km);
    if (mi.interval_type === 'days'  && mi.next_due_date)
      return new Date() >= new Date(mi.next_due_date);
    return false;
  }

  /** A machine's template items followed by whatever maintenance it has due. */
  function buildItems(
    templates: ChecklistTemplate[], templateId: number | null,
    dueItems: MaintenanceInterval[], hrs: number, km: number,
  ): CheckItem[] {
    const items: CheckItem[] = [];
    const tmpl = templates.find(t => t.id === templateId);

    if (tmpl) {
      for (const item of tmpl.items) {
        items.push({
          type: 'template',
          template_item_id: item.id,
          item_text: item.item_text,
          is_required: item.is_required,
          passed: null,
          notes: '',
        });
      }
    }

    for (const mi of dueItems) {
      items.push({
        type: 'maintenance',
        interval_id: mi.id,
        item_text: mi.name,
        is_required: true,
        is_overdue: isOverdue(mi, hrs, km),
        passed: null,
        notes: '',
      });
    }

    return items;
  }

  /**
   * Assemble the per-machine checklists on the way into the last step —
   * here rather than on load, because which implements are going out is not
   * known until the operator has ticked them.
   *
   * An implement's templates and due maintenance are its own, so each is
   * fetched for itself. Its meters come with it: reusing the tractor's hours
   * to decide whether the implement's service is overdue would put a red
   * OVERDUE on a machine nobody has measured.
   */
  async function enterChecklistStep() {
    setStep('checklist');
    setMachineIdx(0);
    setLoadingMachines(true);
    try {
      const primaryHrs = parseFloat(hoursStart) || 0;
      const primaryKm  = parseFloat(kmStart) || 0;
      const built: MachineCheck[] = [{
        equipment_id: eqId,
        name: equipment?.name ?? 'This machine',
        equipment_type: equipment?.equipment_type ?? '',
        role: 'primary',
        templates,
        selectedTemplateId: templates[0]?.id ?? null,
        dueItems,
        items: buildItems(templates, templates[0]?.id ?? null, dueItems, primaryHrs, primaryKm),
      }];

      const implIds = attachments
        .filter(a => selectedImpl.has(a.implement_id))
        .map(a => a.implement_id);

      for (const implId of implIds) {
        const att = attachments.find(a => a.implement_id === implId);
        const [eqRes, tmplRes, dueRes] = await Promise.all([
          equipmentService.getById(implId),
          equipmentService.getChecklistTemplates(implId),
          equipmentService.getDueMaintenanceItems(implId),
        ]);
        const implEq        = eqRes.success ? eqRes.data : null;
        const implTemplates = tmplRes.success && tmplRes.data ? tmplRes.data : [];
        const implDue       = dueRes.success  && dueRes.data  ? dueRes.data  : [];
        const implHrs = toNumOr(implEq?.current_hours);
        const implKm  = toNumOr(implEq?.current_km);
        const firstTemplateId = implTemplates[0]?.id ?? null;

        built.push({
          equipment_id: implId,
          // The attachment row is the fallback, not the source: a machine's
          // own record is the one an operator will recognise on the badge.
          name: implEq?.name ?? att?.implement_name ?? `Implement ${implId}`,
          equipment_type: implEq?.equipment_type ?? att?.implement_type ?? '',
          role: 'implement',
          templates: implTemplates,
          selectedTemplateId: firstTemplateId,
          dueItems: implDue,
          items: buildItems(implTemplates, firstTemplateId, implDue, implHrs, implKm),
        });
      }

      setMachines(built);
    } catch {
      setError('Could not load the pre-start checklists for this rig');
    } finally {
      setLoadingMachines(false);
    }
  }

  function setItemResult(mIdx: number, idx: number, passed: boolean) {
    setMachines(prev => prev.map((m, i) => i !== mIdx ? m : {
      ...m,
      items: m.items.map((item, j) => j === idx ? { ...item, passed } : item),
    }));
  }

  function selectTemplate(mIdx: number, templateId: number) {
    setMachines(prev => prev.map((m, i) => i !== mIdx ? m : {
      ...m,
      selectedTemplateId: templateId,
      // Answers belong to the template they were given against, so switching
      // starts the list again rather than carrying ticks onto other questions.
      items: buildItems(m.templates, templateId, m.dueItems,
                        parseFloat(hoursStart) || 0, parseFloat(kmStart) || 0),
    }));
  }

  const machine = machines[machineIdx] ?? null;
  const checklist = machine?.items ?? [];
  const isLastMachine = machineIdx >= machines.length - 1;

  const allRequiredPassed = checklist.every(item => !item.is_required || item.passed !== null);
  const hasFailures = checklist.some(item => item.is_required && item.passed === false);
  // One failed machine stops the rig. An implement that is unroadworthy is
  // not made safe by the tractor passing its own check.
  const anyFailures = machines.some(m => m.items.some(i => i.is_required && i.passed === false));

  const selectedJob = useMemo(
    () => (selectedJobId != null && selectedJobId !== QUICK_JOB_ID ? jobs.find(j => j.id === selectedJobId) : null),
    [selectedJobId, jobs],
  );

  async function handleStart() {
    if (!equipment || selectedJobId == null) return;
    setSaving(true);
    setError(null);
    try {
      // 1. Resolve (or create) the job this pre-start will feed into.
      let jobId = selectedJobId;
      if (jobId === QUICK_JOB_ID) {
        const label = taskDesc.trim() || `Quick use — ${equipment.name}`;
        const res = await jobService.createQuickJob({
          equipment: {
            primary_id: eqId,
            implement_ids: Array.from(selectedImpl),
          },
          name: label,
          induction_override: selfInduct || undefined,
        });
        if (!res.success || !res.data) throw new Error('Failed to create quick job');
        jobId = res.data.id;
      }

      // 2. Start the job. The backend creates the usage_sessions row and
      //    records one checklist completion per machine.
      //
      //    A machine with no template configured contributes nothing — a
      //    completion has to point at a template, so there is no honest row to
      //    write for it. It is skipped rather than folded into another
      //    machine's record.
      const checklists = machines.flatMap(m => {
        const templateItems = m.items
          .filter(i => i.type === 'template' && i.template_item_id != null)
          .map(i => ({
            template_item_id: i.template_item_id!,
            passed: i.passed ?? true,
            notes: i.notes || undefined,
          }));
        if (!m.selectedTemplateId || templateItems.length === 0) return [];

        const maintenanceItems = m.items
          .filter(i => i.type === 'maintenance' && i.interval_id != null)
          .map(i => ({ interval_id: i.interval_id!, passed: i.passed ?? false }));

        return [{
          equipment_id: m.equipment_id,
          template_id: m.selectedTemplateId,
          items: templateItems,
          maintenance_items: maintenanceItems.length ? maintenanceItems : undefined,
        }];
      });

      const startRes = await jobService.startJob(jobId, {
        hours_start: hoursStart ? parseFloat(hoursStart) : undefined,
        km_start:    kmStart    ? parseFloat(kmStart)    : undefined,
        checklists: checklists.length ? checklists : undefined,
        induction_override: selfInduct || undefined,
      });
      if (!startRes.success) throw new Error('Failed to start job');

      // 3. Land on the operator view — GPS tracking + controls live there.
      navigate(`/jobs/${jobId}/operate`);
    } catch (e: unknown) {
      const refusal = inductionRefusal(e);
      if (refusal) setInductionGaps(refusal.gaps);
      const msg = refusal?.message ?? (e instanceof Error ? e.message : 'Failed to start');
      setError(msg);
      setSaving(false);
    }
  }

  if (loading || !equipment) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Pre-Start Check" />
        <div className="max-w-xl mx-auto px-4 py-6 space-y-3">
          {[0, 1, 2].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  const canProceedFromJob = selectedJobId != null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Pre-Start Check"
        subtitle={equipment.name}
      />

      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">

        {/* Progress */}
        <div className="flex gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className={cn('flex-1 h-1.5 rounded-full transition-colors',
              step === s ? 'bg-green-500' :
              (STEPS.indexOf(step) > i) ? 'bg-green-300' :
              'bg-neutral-200')} />
          ))}
        </div>

        {inductionGaps.length > 0 && (
          <div className="rounded-xl border border-warning-500/40 bg-warning-50 p-4 text-sm text-warning-700 space-y-2">
            <div className="flex items-center gap-2 font-semibold">
              <ShieldAlert className="h-5 w-5 shrink-0" /> You are not cleared to use this
            </div>
            <ul className="list-disc pl-5 space-y-1 break-words">
              {inductionGaps.map((g, i) => (
                <li key={i}>
                  {g.reason === 'certificate'
                    ? `${g.equipment_name}: needs a current ${(g.missing_certificates || []).join(' and ')} on file`
                    : `${g.equipment_name}: ${g.reason === 'expired' ? 'your induction has expired' : 'not inducted'}`}
                </li>
              ))}
            </ul>
            {canSelfInduct && inductionGaps.every(g => g.reason !== 'certificate') ? (
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-0.5" checked={selfInduct} onChange={e => setSelfInduct(e.target.checked)} />
                <span>I am competent to operate this and sign myself off (recorded against my name).</span>
              </label>
            ) : (
              <div className="space-y-2">
                <p>You need an induction from your supervisor before you can start.</p>
                {inductionGaps.some(g => g.reason !== 'certificate') && (
                  asked === 'asked' ? (
                    <p className="font-medium">Your supervisor has been asked to sign you off.</p>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={asked === 'asking'}
                      onClick={async () => {
                        setAsked('asking');
                        try {
                          const ids = [...new Set(inductionGaps.map(g => g.equipment_id))];
                          const job = selectedJobId != null && selectedJobId !== QUICK_JOB_ID ? selectedJobId : null;
                          await inductionService.requestForMe(ids, job);
                          setAsked('asked');
                        } catch {
                          setAsked('failed');
                        }
                      }}
                    >
                      Ask my supervisor to sign me off
                    </Button>
                  )
                )}
                {asked === 'failed' && <p>Could not send the request. Tell your supervisor directly.</p>}
                <p><Link to="/my-inductions" className="underline">My inductions</Link></p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 0: Job ── */}
        {step === 'job' && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2 text-neutral-700">
                <ClipboardList className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold">Select Job</h3>
              </div>
              <p className="text-sm text-neutral-500">
                Which job is this work for? Choose a pending assignment or start a quick-use job.
              </p>

              <div className="space-y-2">
                {jobs.map(j => (
                  <label
                    key={j.id}
                    className={cn('flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                      selectedJobId === j.id ? 'bg-green-50 border-green-400' : 'bg-white border-neutral-200 hover:border-green-300')}
                  >
                    <input type="radio" name="job" value={j.id}
                      checked={selectedJobId === j.id}
                      onChange={() => setSelectedJobId(j.id)}
                      className="mt-1 h-4 w-4 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-neutral-800 truncate">{j.name}</p>
                      {j.target_name && (
                        <p className="text-xs text-neutral-500 truncate">{j.target_name}</p>
                      )}
                    </div>
                  </label>
                ))}

                <label className={cn('flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                  selectedJobId === QUICK_JOB_ID ? 'bg-green-50 border-green-400' : 'bg-white border-neutral-200 hover:border-green-300')}
                >
                  <input type="radio" name="job" value={QUICK_JOB_ID}
                    checked={selectedJobId === QUICK_JOB_ID}
                    onChange={() => setSelectedJobId(QUICK_JOB_ID)}
                    className="mt-1 h-4 w-4 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-neutral-800 flex items-center gap-1">
                      <Plus className="h-3.5 w-3.5" /> Quick use — no planned job
                    </p>
                    <p className="text-xs text-neutral-500">
                      A minimal job will be created so GPS and meter readings are still recorded.
                    </p>
                  </div>
                </label>
              </div>

              <Button className="w-full" onClick={() => setStep('meter')} disabled={!canProceedFromJob}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Step 1: Meter readings ── */}
        {step === 'meter' && (
          <Card>
            <CardContent className="pt-4 space-y-4">
              <div className="flex items-center gap-2 text-neutral-700">
                <Gauge className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold">Meter Readings</h3>
              </div>
              <p className="text-sm text-neutral-500">Record the current meter reading before you start.</p>

              {selectedJob && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 text-sm">
                  <p className="text-neutral-500 text-xs">For job</p>
                  <p className="font-medium text-neutral-800">{selectedJob.name}</p>
                </div>
              )}

              {(equipment.meter_type === 'hours' || equipment.meter_type === 'both') && (
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Hours Meter *</label>
                  <input type="number" step="0.1" min="0" value={hoursStart}
                    onChange={e => setHoursStart(e.target.value)}
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-lg font-bold text-neutral-800 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0.0" />
                </div>
              )}

              {(equipment.meter_type === 'km' || equipment.meter_type === 'both') && (
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Odometer (km) *</label>
                  <input type="number" step="1" min="0" value={kmStart}
                    onChange={e => setKmStart(e.target.value)}
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-lg font-bold text-neutral-800 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0" />
                </div>
              )}

              {selectedJobId === QUICK_JOB_ID && (
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Task Description</label>
                  <input value={taskDesc} onChange={e => setTaskDesc(e.target.value)}
                    placeholder="e.g. Slashing north paddock"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('job')} className="flex-1">Back</Button>
                <Button onClick={() => setStep('implements')} className="flex-1">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Step 2: Implements ── */}
        {step === 'implements' && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center gap-2 text-neutral-700">
                <Wrench className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold">Implements</h3>
              </div>

              {attachments.length === 0 ? (
                <p className="text-sm text-neutral-400">No implements currently attached.</p>
              ) : (
                <div className="space-y-2">
                  {attachments.map(att => (
                    <label key={att.implement_id}
                      className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-neutral-50 transition-colors"
                    >
                      <input type="checkbox"
                        checked={selectedImpl.has(att.implement_id)}
                        onChange={e => {
                          const s = new Set(selectedImpl);
                          if (e.target.checked) { s.add(att.implement_id); } else { s.delete(att.implement_id); }
                          setSelectedImpl(s);
                        }}
                        className="h-4 w-4 rounded text-green-600" />
                      <div>
                        <p className="font-medium text-sm text-neutral-800">{att.implement_name}</p>
                        <p className="text-xs text-neutral-400">{att.implement_type}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* It used to say the pre-start covered the implements too. It
                  does not, and never did: one checklist is run, against this
                  machine's type, and the completion is recorded against this
                  machine. Selected implements get the session and the GPS
                  track. Saying otherwise mattered more once a spray started
                  demanding a check on the sprayer as well — an operator would
                  have been told it was covered and then blocked for it. */}
              <p className="text-xs text-neutral-400">
                Selected implements share this machine's usage session and GPS track,
                and each gets its own pre-start check on the next step.
              </p>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('meter')} className="flex-1">Back</Button>
                <Button onClick={enterChecklistStep} className="flex-1">
                  {templates.length > 0 ? 'Next' : 'Skip to Start'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Step 3: Checklist, one machine at a time ── */}
        {step === 'checklist' && loadingMachines && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-12 bg-neutral-100 rounded-xl animate-pulse" />
              ))}
            </CardContent>
          </Card>
        )}

        {step === 'checklist' && !loadingMachines && (
          <>
            {/* Which machine this is. Rendered even for a lone machine: the
                pre-start is of a named machine, and saying which one is what
                stops an operator answering the tractor's questions while
                standing at the sprayer. */}
            {machine && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-800 truncate">{machine.name}</p>
                      <p className="text-xs text-neutral-500">
                        {machine.role === 'primary' ? 'Prime mover' : 'Implement'}
                        {machine.equipment_type ? ` · ${machine.equipment_type}` : ''}
                      </p>
                    </div>
                    {machines.length > 1 && (
                      <span className="text-xs font-medium text-neutral-500 bg-neutral-100 rounded-full px-2.5 py-1 shrink-0">
                        {machineIdx + 1} of {machines.length}
                      </span>
                    )}
                  </div>

                  {machines.length > 1 && (
                    <div className="flex gap-1 mt-3">
                      {machines.map((m, i) => (
                        <div key={m.equipment_id} className={cn('flex-1 h-1 rounded-full',
                          i < machineIdx ? 'bg-green-300' :
                          i === machineIdx ? 'bg-green-500' : 'bg-neutral-200')} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Template picker */}
            {machine && machine.templates.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {machine.templates.map(t => (
                  <button key={t.id}
                    onClick={() => selectTemplate(machineIdx, t.id)}
                    className={cn('text-sm px-3 py-1.5 rounded-full border font-medium transition-colors',
                      machine.selectedTemplateId === t.id
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-neutral-600 border-neutral-200 hover:border-green-400')}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}

            {/* Maintenance items due banner */}
            {machine && machine.dueItems.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  {machine.dueItems.length} maintenance item{machine.dueItems.length > 1 ? 's' : ''} added from schedule
                </div>
                <p className="text-xs text-amber-600">These are shown below. Tick them off to reset their counter.</p>
              </div>
            )}

            {/* Checklist items */}
            {checklist.length === 0 ? (
              <Card>
                <CardContent className="pt-4 text-center py-8 text-neutral-400">
                  {/* Distinguish "nothing configured" from "nothing to do".
                      A machine with no template records no completion, so
                      anything asking later whether it was checked will say no. */}
                  {machine && machine.templates.length === 0
                    ? <>No pre-start checklist is configured for {machine.equipment_type || 'this equipment type'}.
                        Nothing will be recorded for {machine.name}.</>
                    : <>No checklist items.</>}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-4 space-y-1">
                  {checklist.map((item, idx) => (
                    <div key={idx} className={cn(
                      'flex items-center gap-3 p-3 rounded-xl border transition-colors',
                      item.passed === true  ? 'bg-green-50 border-green-200' :
                      item.passed === false ? 'bg-red-50 border-red-200'    :
                      'bg-white border-neutral-100'
                    )}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-800">{item.item_text}</p>
                        {item.type === 'maintenance' && (
                          <p className={cn('text-xs mt-0.5',
                            item.is_overdue ? 'text-red-500 font-medium' : 'text-amber-600')}>
                            {item.is_overdue ? 'OVERDUE — maintenance required' : 'Service due'}
                          </p>
                        )}
                        {!item.is_required && <p className="text-xs text-neutral-400">Optional</p>}
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => setItemResult(machineIdx, idx, true)}
                          className={cn('h-8 w-8 rounded-lg flex items-center justify-center transition-colors',
                            item.passed === true ? 'bg-green-500 text-white' : 'bg-neutral-100 text-neutral-400 hover:bg-green-100 hover:text-green-600')}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setItemResult(machineIdx, idx, false)}
                          className={cn('h-8 w-8 rounded-lg flex items-center justify-center transition-colors',
                            item.passed === false ? 'bg-red-500 text-white' : 'bg-neutral-100 text-neutral-400 hover:bg-red-100 hover:text-red-600')}
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {anyFailures && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {/* Names the machine, because on a rig "one or more required
                    items failed" leaves the operator to work out which of two
                    machines they are being told not to run. */}
                <span>
                  Required items failed on{' '}
                  {machines.filter(m => m.items.some(i => i.is_required && i.passed === false))
                    .map(m => m.name).join(' and ')}.
                  {' '}Do not operate until resolved.
                </span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => machineIdx > 0 ? setMachineIdx(machineIdx - 1) : setStep('implements')}
                className="flex-1"
              >
                Back
              </Button>
              {isLastMachine ? (
                <Button
                  onClick={handleStart}
                  disabled={!allRequiredPassed || anyFailures || saving || blockedByInduction}
                  isLoading={saving}
                  className="flex-1"
                >
                  <Play className="h-4 w-4 mr-1.5" />
                  Start
                </Button>
              ) : (
                <Button
                  onClick={() => setMachineIdx(machineIdx + 1)}
                  /* Each machine is finished before the next is offered.
                     Letting the operator skip ahead is how an implement ends
                     up with a completion row full of unanswered items. */
                  disabled={!allRequiredPassed || hasFailures}
                  className="flex-1"
                >
                  Next: {machines[machineIdx + 1]?.name}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
